import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Spacer,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BodyOf, ResponseOf } from "../../../core/api";
import useApi from "../../../shared/hooks/useApi";
import { PatientServicePlanId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import ClaimSignatureByEmailButton from "./ClaimSignatureByEmailButton";
import SignatureCanvas from "./SignatureCanvas";

type ServicePlan = ResponseOf<"get", "./service_plans/:patientServicePlanId">;
type SignatureParams = BodyOf<"post", "./service_plans/:patientServicePlanId/signatures">;
type SignatureRole = SignatureParams["role"];

// `canClaim` is the whole switch for emailed signing links — the server, the
// claim table and the signing page are all keyed by role, so turning this on
// for the assessor is a one-line change.
const ROLES: {
  role: SignatureRole;
  title: string;
  planKey: "assessor" | "consumer" | "caseManager";
  isOptional: boolean;
  canClaim: boolean;
}[] = [
  {
    role: "ASSESSOR",
    title: "Assessor (RN / Health coach)",
    planKey: "assessor",
    isOptional: false,
    canClaim: false,
  },
  {
    role: "CONSUMER",
    title: "Consumer / Representative",
    planKey: "consumer",
    isOptional: false,
    canClaim: true,
  },
  {
    role: "CASE_MANAGER",
    title: "Case manager",
    planKey: "caseManager",
    isOptional: true,
    canClaim: false,
  },
];

interface Props {
  isReadOnly: boolean;
  plan: ServicePlan;
  servicePlanId: PatientServicePlanId;
}

export default function ServicePlanSignaturesStep({ isReadOnly, plan, servicePlanId }: Props) {
  return (
    <VStack align="stretch" spacing={5}>
      {!isReadOnly && (
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>
            Each signature saves on its own. Name, date and signature are all required for a
            signature to be stored.
          </AlertDescription>
        </Alert>
      )}

      {ROLES.map((entry) => (
        <SignatureBlock
          key={entry.role}
          canClaim={entry.canClaim}
          claim={plan.signatureClaims.find((claim) => claim.role === entry.role)}
          isOptional={entry.isOptional}
          isReadOnly={isReadOnly}
          role={entry.role}
          servicePlanId={servicePlanId}
          signature={plan[entry.planKey]}
          title={entry.title}
        />
      ))}
    </VStack>
  );
}

function SignatureBlock(props: {
  canClaim: boolean;
  claim: ServicePlan["signatureClaims"][number] | undefined;
  isOptional: boolean;
  isReadOnly: boolean;
  role: SignatureRole;
  servicePlanId: PatientServicePlanId;
  signature: ServicePlan["assessor"];
  title: string;
}) {
  const { canClaim, claim, isOptional, isReadOnly, role, servicePlanId, signature, title } = props;
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState(signature.name ?? "");
  const [signedDate, setSignedDate] = useState(
    signature.signedDate === null ? "" : signature.signedDate.toString()
  );
  const [signatureData, setSignatureData] = useState<string | null>(signature.signatureData);

  const upsertMutation = useMutation({
    ...mutations.servicePlan.upsertSignature(servicePlanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.servicePlan.byId(servicePlanId).queryKey });
      toast({
        title: `${title} signature saved`,
        status: "success",
        position: "top-right",
        isClosable: true,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not save signature",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const missingFields = [
    name.trim() === "" ? "name" : null,
    signedDate === "" ? "date" : null,
    signatureData === null ? "signature" : null,
  ].filter((field): field is string => field !== null);

  const handleSave = () => {
    if (signatureData === null || signedDate === "") {
      return;
    }

    upsertMutation.mutate({
      role,
      name: name.trim(),
      signedDate: LocalDate.parse(signedDate),
      signatureData,
    });
  };

  const isSigned = signature.signatureData !== null;

  return (
    <Box borderRadius="md" borderWidth="1px" padding={4}>
      <HStack marginBottom={3}>
        <Text fontSize="md" fontWeight="semibold">
          {title}
        </Text>
        {isOptional && (
          <Text color="gray.500" fontSize="sm">
            (optional)
          </Text>
        )}
        <Spacer />
        {isSigned && (
          <Text color="green.600" fontSize="sm">
            Signed
          </Text>
        )}
        {canClaim && (
          <ClaimSignatureByEmailButton
            claim={claim}
            isDisabled={isReadOnly || isSigned}
            role={role}
            servicePlanId={servicePlanId}
            title={title}
          />
        )}
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2 }} marginBottom={3} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Name</FormLabel>
          <Input
            isReadOnly={isReadOnly}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Date signed</FormLabel>
          <Input
            isReadOnly={isReadOnly}
            type="date"
            value={signedDate}
            onChange={(event) => setSignedDate(event.target.value)}
          />
        </FormControl>
      </SimpleGrid>

      <SignatureCanvas isDisabled={isReadOnly} value={signatureData} onChange={setSignatureData} />

      {!isReadOnly && (
        <HStack marginTop={3}>
          {missingFields.length > 0 && (
            <Text color="gray.500" fontSize="sm">
              Add {missingFields.join(", ")} to save.
            </Text>
          )}
          <Spacer />
          <Button
            colorScheme="blue"
            isDisabled={missingFields.length > 0}
            isLoading={upsertMutation.isPending}
            onClick={handleSave}
          >
            Save signature
          </Button>
        </HStack>
      )}
    </Box>
  );
}
